import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from "@/components/ui/sheet";
import { CheckCircle, AlertTriangle, Trash2, X, Cookie } from "lucide-react";

export const CookieDevToolSheet = () => {
    const [open, setOpen] = useState(false);
    const [key, setKey] = useState("");
    const [value, setValue] = useState("");
    const [cookies, setCookies] = useState<Record<string, string>>({});
    const [feedback, setFeedback] = useState<null | { message: string; type: "success" | "error" }>(null);

    useEffect(() => {
        if (open) {
            parseCookies();
        }
    }, [open]);

    const isDemo = typeof window !== "undefined" && window.location.host.includes("github.io");
    const isProd = process.env.NODE_ENV === "production";
    if (isProd && !isDemo) return null;

    const parseCookies = () => {
        const parsed: Record<string, string> = {};
        document.cookie.split(";").forEach((cookie) => {
            const [k, ...v] = cookie.trim().split("=");
            if (k && k.trim() !== "") {
                parsed[k] = v.join("=");
            }
        });
        setCookies(parsed);
    };

    const handleSetCookie = () => {
        if (!key) {
            setFeedback({ message: "Please provide a cookie key.", type: "error" });
            return;
        }

        document.cookie = `${key}=${value}; path=/`;
        setFeedback({ message: `Cookie "${key}" set!`, type: "success" });
        parseCookies();
        setTimeout(() => setFeedback(null), 3000);
    };

    const handleClearCookie = () => {
        if (!key) {
            setFeedback({ message: "Please provide a cookie key to clear.", type: "error" });
            return;
        }

        document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        setFeedback({ message: `Cookie "${key}" cleared.`, type: "success" });
        setKey("");
        setValue("");
        parseCookies();
        setTimeout(() => setFeedback(null), 3000);
    };

    const handleSelectCookie = (selectedKey: string) => {
        if (selectedKey === "__new__") {
            setKey("");
            setValue("");
        } else {
            setKey(selectedKey);
            setValue(cookies[selectedKey] || "");
        }
    };

    const handleClearAllCookies = () => {
        if (Object.keys(cookies).length === 0) {
            setFeedback({ message: "No cookies to clear.", type: "error" });
            return;
        }

        Object.keys(cookies).forEach((cookieKey) => {
            document.cookie = `${cookieKey}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        });

        setCookies({});
        setKey("");
        setValue("");
        setFeedback({ message: "All cookies cleared.", type: "success" });

        setTimeout(() => setFeedback(null), 3000);
    };

    return (
        <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
                <Button variant="outline" className="flex items-center gap-2">
                    <Cookie size={16} /> Cookies
                </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[400px] sm:w-[400px]">
                <SheetHeader className="mb-2">
                    <SheetTitle>Manage Cookies</SheetTitle>
                </SheetHeader>

                {feedback && (
                    <div
                        className={`relative flex items-start gap-2 text-sm px-4 py-3 rounded-md border mb-4 transition-colors ${feedback.type === "success"
                            ? "bg-green-100 text-green-800 border-green-300 dark:bg-green-900 dark:text-green-200 dark:border-green-700"
                            : "bg-red-100 text-red-800 border-red-300 dark:bg-red-900 dark:text-red-200 dark:border-red-700"
                            }`}
                    >
                        <div className="flex items-center gap-2">
                            {feedback.type === "success" ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
                            <span>{feedback.message}</span>
                        </div>
                        <button
                            onClick={() => setFeedback(null)}
                            className="absolute right-2 top-2 text-inherit opacity-60 hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-zinc-400 dark:focus-visible:ring-zinc-600 rounded-sm transition"
                            aria-label="Dismiss feedback"
                        >
                            <X size={16} />
                        </button>
                    </div>
                )}

                <div className="py-4 space-y-3">
                    {Object.keys(cookies).length > 0 && (
                        <div className="space-y-1">
                            <Label>Select Cookie</Label>
                            <Select onValueChange={handleSelectCookie} value={key || "__new__"}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select a cookie" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__new__">+ New Cookie</SelectItem>
                                    {Object.entries(cookies)
                                        .filter(([cookieKey]) => cookieKey.trim() !== "")
                                        .map(([cookieKey]) => (
                                            <SelectItem key={cookieKey} value={cookieKey}>
                                                {cookieKey}
                                            </SelectItem>
                                        ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}

                    <div className="space-y-1">
                        <Label htmlFor="cookie-key">Key</Label>
                        <Input
                            id="cookie-key"
                            value={key}
                            onChange={(e) => setKey(e.target.value)}
                            placeholder="e.g. session_token"
                        />
                    </div>

                    <div className="space-y-1">
                        <Label htmlFor="cookie-value">Value</Label>
                        <Input
                            id="cookie-value"
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            placeholder="e.g. abc123"
                        />
                    </div>

                    <div className="flex flex-col gap-2 pt-2">
                        <Button className="w-full" onClick={handleSetCookie}>
                            Set Cookie
                        </Button>
                        <Button
                            variant="outline"
                            className="w-full"
                            onClick={handleClearCookie}
                            disabled={!key}
                        >
                            Clear Cookie
                        </Button>
                        <Button
                            variant="destructive"
                            className="w-full flex items-center justify-center gap-2"
                            onClick={handleClearAllCookies}
                        >
                            <Trash2 size={16} />
                            Clear All Cookies
                        </Button>
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
};
